import { useDeleteSubscriptionMutation, useRunSubscriptionMutation } from '@urc-ui/store/api/subscriptions/subscriptions';
import {
  useRunSubscriptionMutation as useRossRunSubscriptionMutation,
  useDeleteSubscriptionMutation as useRossDeleteSubscriptionMutation
} from '@urc-ui/store/api/ross/subscriptions/subscriptions';
import { useAddSnackbar } from '@urc-ui/hooks/snackbar';
import { useTranslation } from 'react-i18next';
import { logger } from '@urc-ui/services/logger';
import { ActionSource, ActionsProps } from '@urc-ui/hooks/actions/types';
import { useLocation, useNavigate } from 'react-router-dom';
import { getEntityId } from '@urc-ui/store/api/base';
import { isADSPReportCenterEnabled, isUiRossCutoverEnabled } from '@urc-ui/utils/app-utils';
import { getBaseurl } from '@urc-ui/utils/url-utils';
import { MessageType, SnackbarMessage } from '@urc-ui/store/slices/snackbar/types';
import { TAKT_EVENT, TaktLoggerProps, useTaktLogger } from '@urc-ui/hooks/takt-logger';

const useActions = (props: ActionsProps) => {
  const [deleteTrigger] = useDeleteSubscriptionMutation();
  const [deleteRossTrigger] = useRossDeleteSubscriptionMutation();
  const [runTrigger] = useRunSubscriptionMutation();
  const [runTriggerWithRoss] = useRossRunSubscriptionMutation();
  const { addSnackbar } = useAddSnackbar();
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const subscriptionId = props.subscriptionId || props.subscription?.id || '';
  const deleteErrorMessage = `Something went wrong when deleting report for subscription id ${subscriptionId}`;
  let taktProps: TaktLoggerProps = { subscriptionId: subscriptionId };
  if (props.subscription) {
    taktProps = { subscription: props.subscription };
  }
  const { logCustomTaktEvent } = useTaktLogger(taktProps);

  //TODO - Fix List Subscriptions API Call to load with Latest Data from OS
  //SIM - https://sim.amazon.com/issues/RCMRX-10605
  const onRunReportAction = async () => {
    try {
      props.gridApi.showLoadingOverlay();
      if (isUiRossCutoverEnabled()) {
        await runTriggerWithRoss({ subscriptionId })
          .unwrap()
          .catch(error => {
            logger.logFatalMessage(`Something went wrong when running report for subscription id ${subscriptionId}`, error);
            throw error;
          })
          .finally(() => props.gridApi.hideOverlay());
      } else {
        await runTrigger(subscriptionId)
          .unwrap()
          .catch(error => {
            logger.logFatalMessage(`Something went wrong when running report for subscription id ${subscriptionId}`, error);
            throw error;
          })
          .finally(() => props.gridApi.hideOverlay());
      }

      const message = translate('sspa_rcm_ad_reports_create_report_processing', 'Report processing.');
      const urlMessage = translate('sspa_rcm_ad_reports_create_report_view_report_details_text', 'View report details');
      let snackbarMessage: SnackbarMessage = { message, type: MessageType.SUCCESS };
      if (props.source === ActionSource.SUBSCRIPTION_TABLE) {
        snackbarMessage = {
          ...snackbarMessage,
          detailsUrl: `/history/${subscriptionId}${search}`,
          urlMessage: urlMessage
        };
      } else {
        props.gridApi.refreshServerSideStore({ purge: true });
      }

      addSnackbar(snackbarMessage);
      logCustomTaktEvent({ eventName: TAKT_EVENT.RUN_REPORT, source: props.taktEventSource });
    } catch (error) {
      const message = translate('sspa_rcm_ad_reports_create_report_failure', 'Sorry, something went wrong. Try again later.');
      addSnackbar({ message, type: MessageType.ERROR });
    }
  };

  const onDeleteReportAction = async () => {
    try {
      props.gridApi.showLoadingOverlay();
      if (isUiRossCutoverEnabled()) {
        const response = await deleteRossTrigger({ subscriptionIds: [subscriptionId] })
          .unwrap()
          .catch(error => {
            logger.logFatalMessage(deleteErrorMessage, error);
            throw error;
          });
        if (response?.failed?.length) {
          logger.logFatalMessage(deleteErrorMessage);
          throw new Error(deleteErrorMessage);
        }
      } else {
        await deleteTrigger(subscriptionId)
          .unwrap()
          .catch(error => {
            logger.logFatalMessage(deleteErrorMessage, error);
            throw error;
          });
      }

      props.gridApi.refreshServerSideStore({ purge: true });
      props.gridApi.paginationGoToFirstPage();
      const message = translate('sspa_rcm_ad_reports_delete_report_success', 'Report deleted.');
      addSnackbar({ message, type: MessageType.SUCCESS });
      logCustomTaktEvent({ eventName: TAKT_EVENT.DELETE_REPORT, source: props.taktEventSource });
    } catch (error) {
      props.gridApi.hideOverlay();
      const message = translate('sspa_rcm_ad_reports_delete_report_failure', 'Sorry, something went wrong. Try again later.');
      addSnackbar({ message, type: MessageType.ERROR });
    }
  };

  const onCopyReportAction = () => {
    if (isADSPReportCenterEnabled()) {
      window.location.assign(`${getBaseurl()}/dsp/${getEntityId()}/report/report-center/${subscriptionId}/clone`);
    } else {
      const url = `/copy/${subscriptionId}${search}`;
      logger.logInfoMessage(
        `Actions DropDown - navigating to url: ${url}, subscriptionId: ${subscriptionId}, searchQueryString: ${search}`
      );
      navigate(url);
    }
    logCustomTaktEvent({ eventName: TAKT_EVENT.COPY_REPORT, source: props.taktEventSource });
  };
  return { onRunReportAction, onDeleteReportAction, onCopyReportAction };
};

export { useActions };
